import { Trans } from "@lingui/react/macro"
import { IconFlag } from "@tabler/icons-react"
import { useMemo } from "react"
import { EditableAnalysisField } from "../../../EditableAnalysisField"
import { resolveSeekTime } from "../../helpers"
import { SeekControl } from "../../parts/SeekControl"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/**
 * DecisionsCard renders the analyser's flat list of decisions. Each row gets
 * a serial counter (CSS ::before — decisions are the meeting's durable record,
 * so "decision 02" is referenceable), the decision text, and (when a streaming
 * segment matches by word overlap) a SeekIcon that jumps the audio player to
 * that moment.
 *
 * Returns `null` when the input list is empty so the parent can render the
 * cards-grid without an empty placeholder.
 */
export function DecisionsCard({ decisions, segments, seekOffsets, editContext }: IProps): React.JSX.Element | null {
  const seekTimes = useMemo(
    () => decisions.map((d, i) => resolveSeekTime(seekOffsets?.[i], d, segments)),
    [decisions, segments, seekOffsets],
  )
  if (!decisions || decisions.length === 0) return null
  return (
    <section className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <IconFlag size={16} />
          <Trans>Decisions</Trans>
        </h2>
        <span className={classes.cardCount}>{decisions.length}</span>
      </div>
      <ul className={classes.decisionsList}>
        {decisions.map((d, i) => {
          const path = `decisions.${i}`
          return (
            <li key={`${i}-${d}`} className={classes.decisionsItem}>
              <span className={classes.seekableInline}>
                {editContext ? (
                  <EditableAnalysisField
                    meetingId={editContext.meetingId}
                    fieldPath={path}
                    value={d}
                    canEdit={editContext.canEdit}
                    isEdited={!!editContext.fieldEdits[path]}
                    onSaved={editContext.onEdited}
                  />
                ) : (
                  d
                )}
                <SeekControl seekTime={seekTimes[i]} />
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
